/* eslint-disable react/jsx-props-no-spreading */
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTransition, animated } from '@react-spring/web';
import classNames from 'classnames';
import { useCallback, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import useGameState from '../../hooks/useGameState';

import { useThemeClassName } from '../../contexts/ThemeContext';
import StyledButton from '../buttons/StyledButton';
import QuestionsIndicator from '../lists/QuestionsIndicator';
import QuestionsResults from '../lists/QuestionsResults';
import Loading from '../partials/Loading';
import Question from '../questions/Question';
import BaseGame, { GameApi } from './Base';

import styles from '../../../styles/games/quiz.module.scss';

interface QuizGameProps {
    game: QuizGame;
    participation?: Participation;
    canPlay: boolean;
    reload?: () => void;
    className?: string;
}

function QuizGame({ game, participation, canPlay, reload, className }: QuizGameProps) {
    const themeClassName = useThemeClassName(styles);
    const { id: gameId, questions = null, theme: gameTheme } = game || {};
    const { answers: userAnswers = [] } = participation || {};
    const {
        withNextButton = false,
        nextQuestionTimeout = 0,
        minimumValidAnswers = null,
        questionsIndicatorWithProgress = true,
        questionsIndicatorIconComponent = null,
        questionsIndicatorCurrentIcon = null,
        withQuestionsResults = true,
        questionsResultsItemComponent = null,
        questionsResultsWithBody = true,
    } = gameTheme || {};

    const [loading, setLoading] = useState(false);

    const { currentQuestionIndex: wantedQuestionIndex, allAnswersValid } = useGameState(
        game,
        participation,
    );
    const { answers = [] } = participation || {};
    const validAnswersCount = (answers || []).filter(({ valid = null }) => valid === true).length;
    const isValid =
        minimumValidAnswers !== null ? validAnswersCount >= minimumValidAnswers : allAnswersValid;

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(wantedQuestionIndex);

    const apiRef = useRef<GameApi>(null);

    const onQuestionAnswer = useCallback(
        (questionId, answerChoiceId: string) => {
            const { answerQuestion } = apiRef.current!;
            answerQuestion(questionId, answerChoiceId);
        },
        [gameId],
    );

    const onClickNext = useCallback(() => {
        setCurrentQuestionIndex(wantedQuestionIndex);
    }, [setCurrentQuestionIndex, wantedQuestionIndex]);

    useEffect(() => {
        if (withNextButton) {
            return;
        }
        const timeout = setTimeout(() => {
            setCurrentQuestionIndex(wantedQuestionIndex);
        }, nextQuestionTimeout);
        return () => {
            clearTimeout(timeout);
        };
    }, [wantedQuestionIndex, nextQuestionTimeout]);

    const transitions = useTransition(questions[currentQuestionIndex], {
        initial: { x: '0%' },
        from: { x: '100%' },
        enter: { x: '0%' },
        leave: { x: '-100%', position: 'absolute' },
        keys: (question) => question.id,
    });

    return (
        <BaseGame
            game={game}
            participation={participation}
            canPlay={canPlay}
            isValid={isValid}
            reload={reload}
            apiRef={apiRef}
            onLoadingChange={setLoading}
            withFeedback
            endChildren={
                withQuestionsResults ? (
                    <QuestionsResults
                        withQuestionBody={questionsResultsWithBody}
                        itemComponent={questionsResultsItemComponent}
                        questions={questions}
                        answers={userAnswers}
                    />
                ) : null
            }
            className={classNames([styles.container, themeClassName, className])}
            headerClassName={styles.header}
        >
            <div className={styles.game}>
                <div className={classNames([styles.questions, 'game-questions'])}>
                    {transitions((style, question) => {
                        const { id: questionId = null } = question || {};

                        const questionIndex = questions.findIndex(({ id }) => id === questionId);
                        const answer =
                            (userAnswers || []).find(({ question }) => question === questionId) ||
                            null;
                        return (
                            <animated.div
                                style={style}
                                className={classNames([styles.question, 'game-question'])}
                                data-question-index={questionIndex}
                            >
                                <div className={styles.inner}>
                                    <Question
                                        question={question}
                                        answer={answer}
                                        onAnswer={(newAnswer) =>
                                            onQuestionAnswer(questionId, newAnswer)
                                        }
                                        className={styles.choices}
                                    />
                                    {loading ? (
                                        <Loading loading={loading} className={styles.loading} />
                                    ) : null}
                                </div>
                                {withNextButton ? (
                                    <StyledButton
                                        onClick={onClickNext}
                                        disabled={answer === null}
                                        className={classNames([
                                            styles.nextButton,
                                            {
                                                [styles.disabled]: answer === null,
                                            },
                                        ])}
                                    >
                                        <span className={styles.label}>
                                            <FormattedMessage
                                                defaultMessage="Next"
                                                description="Button label"
                                            />
                                        </span>
                                        <span className={styles.icon}>
                                            <FontAwesomeIcon icon={faChevronRight} />
                                        </span>
                                    </StyledButton>
                                ) : null}
                            </animated.div>
                        );
                    })}
                </div>

                <QuestionsIndicator
                    currentIndex={currentQuestionIndex}
                    questions={questions}
                    answers={userAnswers}
                    className={styles.navigation}
                    iconComponent={questionsIndicatorIconComponent}
                    currentIconComponent={questionsIndicatorCurrentIcon}
                    withProgress={questionsIndicatorWithProgress}
                />
            </div>
        </BaseGame>
    );
}

export default QuizGame;
